<template>
  <v-col :cols="attrs.cols">
    <div class="structure-col">
      <!-- Control Buttons -->


      <div class="structure-col__header">
        <!-- Group Header -->
        <div class="d-flex justify-space-between align-items-center mb-4">
          <div>
            <p class="text-h5 mb-1">{{ item.title }}</p>
            <p v-if="attrs.note">{{ item.description }}</p>
            <p class="text-caption mb-0">
              {{ childCount }} item{{ childCount === 1 ? '' : 's' }}
            </p>
          </div>
          <div v-if="attrs.add && attrs.controls" class="structure-col__controls mb-4">

            <v-btn light icon x-small title="Edit" @click="handleEditGroup">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn light icon x-small title="Delete" @click="handleDeleteList">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>



          </div>
        </div>

        <!-- Add Card Button -->
        <div v-if="attrs.controls && attrs.add" class="mb-4">
          <v-btn color="amber" light rounded small @click="handleAddCard">
            +
          </v-btn>
        </div>

        <!-- Card List -->
        <nested-draggable :key="`nested-${item.id}`" :children="localChildren" :resolved-children="resolvedChildren"
          :cards-by-id="cardsById" :attrs="attrs" :depth="0" :id="item.id" :local="attrs.local"
          :hide-children="isCompact" @editCard="handleEditCard" @deleteCard="handleDeleteCard"
          @cloneCard="handleCloneCard" @addCard="handleAddCardToGroup" @cardMoved="handleCardMoved"
          @update:children="onChildrenUpdate" />
      </div>
    </div>
  </v-col>
</template>

<script>
import NestedDraggable from '@/views/user/exercises/offer-system-tabs/NestedDraggable'

export default {
  name: 'StructureColTabs',

  components: {
    NestedDraggable,
  },

  props: {
    // Array of node objects: [{ id, children }, ...]
    value: {
      type: Array,
      default: () => [],
    },

    // Group/layout object
    item: {
      type: Object,
      default: () => ({}),
    },

    // Card lookup map: { [cardId]: cardDoc }
    cardsById: {
      type: Object,
      default: () => ({}),
    },

    attrs: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isCompact: false,
      // Local copy to avoid direct prop mutation
      localChildren: this.cloneChildren(this.value),
    }
  },

  computed: {
    childCount() {
      return this.localChildren?.length || 0
    },

    // Resolve node IDs to full card objects for display
    resolvedChildren() {
      if (!this.localChildren?.length) return []

      return this.localChildren
        .map((node) => {
          if (!node?.id) return null
          return this.cardsById[node.id] || null
        })
        .filter(Boolean)
    },
  },

  watch: {
    // Sync from parent - use custom comparison to avoid unnecessary updates
    value: {
      handler(newVal) {
        const newStr = JSON.stringify(newVal)
        const localStr = JSON.stringify(this.localChildren)
        if (newStr !== localStr) {
          this.localChildren = this.cloneChildren(newVal)
        }
      },
      deep: true,
    },

    // Emit changes to parent - debounced to prevent rapid updates
    localChildren: {
      handler(newVal) {
        // Only emit if actually different from prop
        const newStr = JSON.stringify(newVal)
        const propStr = JSON.stringify(this.value)
        if (newStr !== propStr) {
          this.$emit('input', newVal)
        }
      },
      deep: true,
    },
  },

  methods: {
    cloneChildren(arr) {
      if (!Array.isArray(arr)) return []
      try {
        const cloned = JSON.parse(JSON.stringify(arr))
        // Ensure all nodes have children arrays for nesting support
        const ensureChildren = (nodes) => {
          for (const node of nodes) {
            if (node && typeof node === 'object') {
              if (!Array.isArray(node.children)) {
                node.children = []
              } else {
                ensureChildren(node.children)
              }
            }
          }
        }
        ensureChildren(cloned)
        return cloned
      } catch (e) {
        console.error('[StructureCol] Failed to clone children:', e)
        return []
      }
    },

    toggleCompact() {
      this.isCompact = !this.isCompact
    },

    handleEditGroup() {
      this.$emit('editGroup', this.item.id)
    },

    handleDeleteList() {
      this.$emit('onDeleteList', { id: this.item.id })
    },

    handleAddCard() {
      this.$emit('addCard', this.item.id)
    },

    handleAddCardToGroup(groupId) {
      this.$emit('addCard', groupId)
    },

    handleEditCard(cardId) {
      this.$emit('editCard', cardId)
    },

    handleDeleteCard(cardId) {
      this.$emit('deleteCard', cardId)
    },

    handleCloneCard(cardId) {
      this.$emit('cloneCard', cardId)
    },

    handleCardMoved(cardId) {
      // Emit with both cardId and the group this card was moved into
      this.$emit('cardMoved', { cardId, groupId: this.item.id })
    },

    onChildrenUpdate(newChildren) {
      // Update localChildren when nested draggable emits changes
      // This ensures nested structures are captured
      this.localChildren = newChildren
    },
  },
}
</script>

<style lang="scss" scoped>
.structure-col {
  color: black;
  background: white;
  padding: 1rem;

  &__controls {
    display: flex;
    flex-wrap: wrap;
  }
}
</style>